import React, { useMemo } from 'react';
import { AutoSizer, Table, Column, InfiniteLoader } from 'react-virtualized';
import { useTranslation } from 'react-i18next';
import { Button, Space, Empty, Spin } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { LoadSheet } from '../../../../types/loadSheets';
import {
  getPageLimit,
  getTimezoneOffset,
  localDateString,
  localTimeString,
} from '../../../utils/Utils';

const ROW_HEIGHT = 40;
const COL_WIDTH = 430;

interface LoadSheetsTableProps {
  loadSheets: LoadSheet[];
  fetchingLoadSheets: boolean;
  receiver: boolean;
  fetchNextLoadSheets: (page: number) => Promise<any>;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onPreview: (id: number, templateName: string) => void;
}

const LoadSheetsTable: React.FC<LoadSheetsTableProps> = ({
  loadSheets,
  fetchingLoadSheets,
  receiver,
  fetchNextLoadSheets,
  onEdit,
  onDelete,
  onPreview,
}) => {
  const { t } = useTranslation();

  const timezoneOffset = React.useMemo(() => getTimezoneOffset(), []);

  const columns = [
    { name: t('loadSheets:receiver'), title: 'receiver' },
    { name: t('loadSheets:templateName'), title: 'template_name' },
    {
      name: `${t('loadSheets:dateAdded')} (${timezoneOffset})`,
      title: 'date_added',
    },
    {
      name: `${t('loadSheets:dateUpdated')} (${timezoneOffset})`,
      title: 'date_updated',
    },
    {
      name: `${t('loadSheets:status')}`,
      title: 'status',
    },
    {
      name: t('loadSheets:actions'),
      title: t('common:action'),
      render: (text: string, record: LoadSheet) => (
        <Space size="small">
          {receiver && (
            <>
              <Button
                type="primary"
                size="small"
                ghost
                icon={<EditOutlined />}
                onClick={() => onEdit(record.id)}
              >
                {t('loadSheets:edit')}
              </Button>
              <Button
                danger
                size="small"
                ghost
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record.id)}
              >
                {t('loadSheets:delete')}
              </Button>
            </>
          )}
          <Button
            type="primary"
            size="small"
            ghost
            icon={<EyeOutlined />}
            onClick={() => onPreview(record.id, record.template_name)}
          >
            {t('loadSheets:preview')}
          </Button>
        </Space>
      ),
      visible: true,
    },
  ];

  const pageSize = useMemo(getPageLimit, []);
  const getRow = ({ index }: any) => {
    return loadSheets[index];
  };
  const handleTableScroll = ({
    startIndex,
    stopIndex,
  }: {
    startIndex: number;
    stopIndex: number;
  }) => {
    const nextPage = Math.ceil(stopIndex / pageSize);
    const currentPage = Math.ceil(startIndex / pageSize);
    const lastPage = currentPage > startIndex / pageSize;

    if (!lastPage && !fetchingLoadSheets) return fetchNextLoadSheets(nextPage);
    return Promise.resolve();
  };

  if (fetchingLoadSheets) {
    return <Spin className="spinner-center" style={{ marginTop: '20px' }} />;
  }

  return (
    <div className="load-sheet__wrapper">
      <AutoSizer>
        {({ width, height }) => (
          <div style={{ height, width }}>
            <div className="load-sheet__log-table">
              <InfiniteLoader
                isRowLoaded={({ index }) => !!loadSheets[index]}
                loadMoreRows={handleTableScroll}
                rowCount={loadSheets.length + pageSize}
                minimumBatchSize={pageSize}
              >
                {({ onRowsRendered, registerChild }) => (
                  <React.Fragment>
                    <Table
                      width={width - 2}
                      height={height - 1}
                      onRowsRendered={onRowsRendered}
                      ref={registerChild}
                      headerHeight={ROW_HEIGHT}
                      rowHeight={ROW_HEIGHT}
                      rowCount={loadSheets.length}
                      rowGetter={getRow}
                      noRowsRenderer={() =>
                        fetchingLoadSheets ? (
                          <Spin className="spinner-center" style={{ marginTop: '20px' }} />
                        ) : (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            className="translation-import-export"
                            description={t('loadSheets:noLoadSheets')}
                          />
                        )
                      }
                    >
                      {columns.map(column => (
                        <Column
                          key={column.title}
                          label={column.name}
                          dataKey={column.title}
                          width={COL_WIDTH}
                          cellRenderer={({ cellData, rowData }) => {
                            if (column.render) {
                              return column.render(cellData, rowData);
                            }
                            if (column.title === 'date_added') {
                              return `${localDateString(rowData.date_added)} - ${localTimeString(
                                rowData.date_added
                              )}`;
                            }
                            if (column.title === 'date_updated') {
                              return `${localDateString(rowData.date_updated)} - ${localTimeString(
                                rowData.date_updated
                              )}`;
                            }
                            return cellData || '';
                          }}
                        />
                      ))}
                    </Table>

                    <Spin
                      spinning={fetchingLoadSheets && loadSheets.length > 0}
                      style={{
                        left: width / 2,
                        position: 'absolute',
                        bottom: '20px',
                      }}
                    />
                  </React.Fragment>
                )}
              </InfiniteLoader>
            </div>
          </div>
        )}
      </AutoSizer>
    </div>
  );
};

export default LoadSheetsTable;
